import * as Yup from "yup";
import type { Form8857Data } from "@/features/forms/types";

/**
 * Shared field metadata for the Spouse-Relief (Form 8857) wizard: the blob key
 * groupings, the empty baseline, an initial-values normalizer, the client-side
 * (UX) validation schema, and the monthly income/expense key lists that Part IV
 * sums for its live totals. Keys are the literal snake_case blob keys the backend
 * mapper reads (`apps/forms/mappings/form_8857.py`).
 */

export const INCOME_VALUE_KEYS: (keyof Form8857Data)[] = [
  "month_gifts",
  "month_wages",
  "month_pensions",
  "month_unemployment",
  "month_social_security",
  "month_government_assistant",
  "month_alimony",
  "month_child_support",
  "month_self_employment_business_income",
  "month_rental_income",
  "month_interest_dividend",
  "other_income_type_val1",
  "other_income_type_val2",
  "other_income_type_val3",
];

export const EXPENSE_VALUE_KEYS: (keyof Form8857Data)[] = [
  "month_exp_food",
  "month_exp_house_supply",
  "month_exp_clothing_services",
  "month_exp_personel_care",
  "month_exp_auto_loan",
  "month_exp_pub_trans",
  "month_exp_rent_mort",
  "month_exp_real_estate",
  "month_exp_electric_gas",
  "month_exp_telephone",
  "month_exp_cab_int",
  "month_exp_health",
  "month_exp_out_pocket",
  "month_exp_other_child_dep",
  "month_exp_other_care",
  "month_exp_other_income_tax",
  "month_exp_other_estimate_tax",
  "month_exp_other_term_insur",
  "month_exp_other_retr_emp",
  "month_exp_other_retr_volt",
  "month_exp_other_union",
  "month_exp_other_unpaid_local",
  "month_exp_other_stu_loan",
  "month_exp_type_val1",
  "month_exp_type_val2",
  "month_exp_type_val3",
  "month_exp_misc",
];

const MONEY_KEYS = [...INCOME_VALUE_KEYS, ...EXPENSE_VALUE_KEYS];

const BOOL_KEYS = new Set<keyof Form8857Data>([
  "diff_add_chk",
  "irs_leave_voicemail",
  "remove_code",
  "check_box_refund",
]);

/** Every blob key defaulted (strings/choices → "", checkboxes → false). */
export const EMPTY_8857: Form8857Data = {
  para_desc: "",
  irs_take_share: "",
  tax_year1: "", tax_year2: "", tax_year3: "", tax_year4: "", tax_year5: "", tax_year6: "",
  prefer_english: "",
  other_language: "",
  diff_add_chk: false,
  diff_add1: "",
  curr_spouce_apt: "",
  curr_spouce_county: "",
  diff_add2: "",
  client_day_time_phone: "",
  irs_leave_voicemail: false,
  curr_spouce: "",
  curr_spouce_ssn: "",
  curr_spouce_add1: "",
  curr_spouce_apt2: "",
  curr_spouce_add2: "",
  sp_day_time_phone: "",
  marital_status: "",
  married_apart_since: "",
  widowed_since: "",
  ligally_separate_since: "",
  divorce_since: "",
  high_education: "",
  list_degree: "",
  business_course: "",
  education_explain: "",
  mental_physical_health: "",
  health_explain: "",
  afraid_to_provide: "",
  file_joint_return: "",
  file_return_explain: "",
  inv_prep_ret_exp: "",
  person_income_know_exp: "",
  ret_missed_info_exp: "",
  show_bal_due_yes_exp: "",
  finance_problem_yes_exp: "",
  household_finance_exp: "",
  incr_large_expense: "",
  incr_large_expense_exp: "",
  transferred_assets: "",
  transferred_assets_exp: "",
  adults: "",
  children: "",
  month_gifts: "",
  month_wages: "",
  month_pensions: "",
  month_unemployment: "",
  month_social_security: "",
  month_government_assistant: "",
  month_alimony: "",
  month_child_support: "",
  month_self_employment_business_income: "",
  month_rental_income: "",
  month_interest_dividend: "",
  other_income_type1: "", other_income_type_val1: "",
  other_income_type2: "", other_income_type_val2: "",
  other_income_type3: "", other_income_type_val3: "",
  month_exp_food: "",
  month_exp_house_supply: "",
  month_exp_clothing_services: "",
  month_exp_personel_care: "",
  month_exp_auto_loan: "",
  month_exp_pub_trans: "",
  month_exp_rent_mort: "",
  month_exp_real_estate: "",
  month_exp_electric_gas: "",
  month_exp_telephone: "",
  month_exp_cab_int: "",
  month_exp_health: "",
  month_exp_out_pocket: "",
  month_exp_other_child_dep: "",
  month_exp_other_care: "",
  month_exp_other_income_tax: "",
  month_exp_other_estimate_tax: "",
  month_exp_other_term_insur: "",
  month_exp_other_retr_emp: "",
  month_exp_other_retr_volt: "",
  month_exp_other_union: "",
  month_exp_other_unpaid_local: "",
  month_exp_other_stu_loan: "",
  month_exp_type1: "", month_exp_type_val1: "",
  month_exp_type2: "", month_exp_type_val2: "",
  month_exp_type3: "", month_exp_type_val3: "",
  month_exp_misc: "",
  sp_family_victim: "",
  remove_code: false,
  abuse_explain: "",
  afraid_of_persion: "",
  pose_danger: "",
  any_other_information: "",
  check_box_refund: false,
};

/** Merge a stored `data` blob onto the empty baseline, coercing checkboxes. */
export function toInitialValues8857(data: Record<string, unknown>): Form8857Data {
  const d = data as Partial<Form8857Data>;
  const out = { ...EMPTY_8857 };
  (Object.keys(EMPTY_8857) as (keyof Form8857Data)[]).forEach((k) => {
    if (BOOL_KEYS.has(k)) {
      (out[k] as boolean) = Boolean(d[k]);
    } else if (d[k] !== undefined && d[k] !== null) {
      (out[k] as string) = String(d[k]);
    }
  });
  return out;
}

const MONEY_PATTERN = /^\d*\.?\d*$/;
const INTEGER_PATTERN = /^\d*$/;
const ssnDigits = (v: string | undefined) => (v ?? "").replace(/\D/g, "").length;

const moneyRules = Object.fromEntries(
  MONEY_KEYS.map((key) => [key, Yup.string().matches(MONEY_PATTERN, "Enter a valid amount.")]),
);

/**
 * Client-side (UX) validation. The server re-validates format/enum/type/SSN in
 * the mapper; conditional-required rules here are UX only so incremental autosave
 * of a partly-filled blob is never rejected server-side.
 */
export const schema8857 = Yup.object({
  curr_spouce_ssn: Yup.string().test("ssn", "Enter a 9-digit SSN.", (v) => {
    const n = ssnDigits(v);
    return n === 0 || n === 9;
  }),
  adults: Yup.string().matches(INTEGER_PATTERN, "Whole number only."),
  children: Yup.string().matches(INTEGER_PATTERN, "Whole number only."),
  other_language: Yup.string().when("prefer_english", {
    is: "N",
    then: (s) => s.required("Enter the preferred language."),
  }),
  diff_add1: Yup.string().when("diff_add_chk", {
    is: true,
    then: (s) => s.required("Enter the contact address."),
  }),
  list_degree: Yup.string().when("high_education", {
    is: "H",
    then: (s) => s.required("List the degree(s)."),
  }),
  married_apart_since: Yup.string().when("marital_status", {
    is: "A",
    then: (s) => s.required("Enter the date."),
  }),
  widowed_since: Yup.string().when("marital_status", {
    is: "W",
    then: (s) => s.required("Enter the date."),
  }),
  ligally_separate_since: Yup.string().when("marital_status", {
    is: "S",
    then: (s) => s.required("Enter the date."),
  }),
  divorce_since: Yup.string().when("marital_status", {
    is: "D",
    then: (s) => s.required("Enter the date."),
  }),
  ...moneyRules,
});
